import { useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, ShieldCheck, LockSimple } from "@phosphor-icons/react";
import { api, act, useFetch, fmtDate } from "@/lib/api";
import { PageHeader, Panel, Badge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const Check = ({ c }) => (
  <div className="flex items-center gap-3 py-2" data-testid={`golive-check-${c.key}`}>
    {c.ok ? <CheckCircle size={18} weight="fill" className="text-[#00C781]" /> : <XCircle size={18} weight="fill" className="text-[#FF3B30]" />}
    <span className={`text-sm ${c.ok ? "" : "text-[#FF6961]"}`}>{c.label}</span>
  </div>
);

const Item = ({ i, onSign }) => (
  <label className="flex items-start gap-3 py-3 cursor-pointer" data-testid={`golive-item-${i.kind}-${i.id}`}>
    <Checkbox className="mt-0.5" data-testid={`golive-sign-${i.kind}-${i.id}`} checked={i.signed} disabled={i.blockers.length > 0} onCheckedChange={(v) => onSign(i, !!v)} />
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium flex items-center gap-2 flex-wrap">{i.title}<Badge v={i.kind} tone="blue" />{i.stale && <Badge v="changed since sign-off" tone="amber" />}</div>
      <div className="text-xs text-zinc-500">{i.detail}{i.signed && ` · signed by ${i.signed_by} ${fmtDate(i.signed_at)}`}</div>
      {i.blockers.map((b) => <div key={b} className="text-xs text-[#FF6961] mt-0.5">{b}</div>)}
    </div>
  </label>
);

export default function GoLive() {
  const nav = useNavigate();
  const { data, setData } = useFetch("/golive");
  const sign = async (i, signed) => { const r = await act(() => api.post("/golive/signoff", { kind: i.kind, id: i.id, signed })); if (r) setData(r); };
  const goLive = async () => {
    const c = window.prompt("Live mode emails REAL prospects from the shared mailbox. Type GO LIVE to confirm.");
    if (c !== "GO LIVE") return;
    const r = await act(() => api.patch("/delivery", { mode: "live", confirm: c }), "Live delivery is on");
    if (r) nav("/settings");
  };
  if (!data) return <div className="text-zinc-500 font-mono text-sm caret">Loading checklist</div>;
  const signed = data.items.filter((i) => i.signed).length;
  return (
    <div data-testid="golive-page">
      <PageHeader eyebrow="Final sign-off" title="Go-live checklist" desc="Live mode sends real emails to real prospects. Every automatic check must pass and you must sign off each campaign and product. Editing one later clears its sign-off.">
        <Badge v={`mode: ${data.mode}`} tone={data.mode === "live" ? "red" : "blue"} className="self-center" />
        <Button data-testid="golive-switch-btn" disabled={!data.ready || data.mode === "live"} onClick={goLive} className={`gap-1 ${data.ready ? "bg-[#FF3B30] hover:bg-[#FF3B30]/80" : "bg-zinc-800 text-zinc-400 disabled:opacity-100"}`}>{data.ready ? <ShieldCheck /> : <LockSimple />} {data.ready ? "Switch to Live" : "Live locked"}</Button>
      </PageHeader>
      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-3">
        <Panel title="Automatic checks"><div className="divide-y divide-border/60">{data.checks.map((c) => <Check key={c.key} c={c} />)}</div></Panel>
        <Panel title={`Your sign-off · ${signed}/${data.items.length}`}>
          {!data.items.length ? <div className="text-sm text-zinc-500">No campaigns or approved products yet.</div>
            : <div className="divide-y divide-border/60">{data.items.map((i) => <Item key={`${i.kind}-${i.id}`} i={i} onSign={sign} />)}</div>}
        </Panel>
      </div>
      <div className="mt-4 text-sm" data-testid="golive-status">{data.ready ? <span className="text-[#00C781]">Everything is signed off — you can switch to Live.</span> : <span className="text-zinc-400">Live stays locked until every check passes and every item is signed.</span>}</div>
    </div>
  );
}
